/**
 * FlahaSOIL v2 — logout page (Phase 9A-G).
 *
 * Calls `actions.logout()` exactly once on mount. AuthProvider attempts server
 * revocation and always drops local session state; once the lifecycle becomes
 * unauthenticated this route redirects to `/login` without a timer.
 */

import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "../auth";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { reducedMotionMediaQuery } from "../theme/motionTokens";

export function LogoutPage() {
	const { actions, status } = useAuth();
	const firedRef = useRef(false);

	useEffect(() => {
		if (firedRef.current) return;
		firedRef.current = true;
		void actions.logout();
	}, [actions]);

	if (status === "unauthenticated") {
		return <Navigate to="/login" replace />;
	}

	return (
		<Box
			sx={{
				display: "grid",
				placeItems: "center",
				minHeight: "60vh",
				p: 2,
			}}
		>
			<GlassSurface
				role="status"
				aria-live="polite"
				aria-label="Signing out"
				intensity="strong"
				depth="floating"
				sx={{ width: "min(100%, 440px)", p: { xs: 3, sm: 4 } }}
			>
				<Stack spacing={2} alignItems="center" textAlign="center">
					<CircularProgress
						aria-hidden="true"
						sx={{
							color: "workspace.green",
							[reducedMotionMediaQuery]: {
								animation: "none",
								"& .MuiCircularProgress-circle": { animation: "none" },
							},
						}}
					/>
					<Box>
						<Typography component="h1" variant="h5">
							Ending your session
						</Typography>
						<Typography variant="body2" sx={{ mt: 0.75, color: "workspace.textSecondary" }}>
							Clearing this browser’s authenticated FlahaSOIL session…
						</Typography>
					</Box>
				</Stack>
			</GlassSurface>
		</Box>
	);
}
